/**
 * AddMemberDialog — bring a member into a conversation (conversation-members spec): a persona (an agent template;
 * account personas such as @krown are personas too), an employee, a person of the organization, or a person's own
 * agent key — an external agent, which joins only a conversation its person is in. Joining runs nothing: a member
 * works only on a message addressed to it. Demo page: /manage-team (its add-member dialog), built on `Modal`, segmented
 * `Tabs`, `SearchBar` and `ItemList`.
 */
import type { ConversationMemberRef, ConversationMemberType } from '@confluent/shared/lib/conversation-addressing';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { useState } from 'react';
import {
  inviteToConversationMutation,
  listApiKeysOptions,
  listConversationMembersQueryKey,
  listEmployeesOptions,
  listOrgMembersOptions,
  listTemplatesOptions,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import { useAuthStore } from '../../store/index.js';
import { Avatar } from '../ui/Avatar.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { EmptyState } from '../ui/EmptyState.js';
import { ItemList } from '../ui/ItemList.js';
import { Modal } from '../ui/Modal.js';
import { SearchBar } from '../ui/SearchBar.js';
import { SkeletonList } from '../ui/Skeleton.js';
import { Tabs } from '../ui/Tabs.js';
import { useToast } from '../ui/Toast.js';

type JoinableType = Exclude<ConversationMemberType, 'agent_instance'>;

interface Candidate {
  id: number;
  name: string;
  detail: string | null;
}

interface AddMemberDialogProps {
  conversationId: string;
  open: boolean;
  onClose: () => void;
  /** Who is in the conversation already: they are not offered again. */
  members: readonly ConversationMemberRef[];
}

const PAGE = { limit: 100, sort_by: 'name', sort_order: 'asc' } as const;

function matches(candidate: Candidate, search: string): boolean {
  const wanted = search.trim().toLowerCase();
  if (!wanted) return true;
  return `${candidate.name} ${candidate.detail ?? ''}`.toLowerCase().includes(wanted);
}

export function AddMemberDialog({ conversationId, open, onClose, members }: AddMemberDialogProps) {
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((state) => state.user?.organizationId ?? null);
  const canSeePeople = usePolicy('CanViewOrg');
  const canSeeKeys = usePolicy('CanManageWorkerSdkKeys');
  const [source, setSource] = useState<JoinableType>('persona');
  const [search, setSearch] = useState('');

  // The search goes to the server: an organization holds more personas than one page (128 in Conflui), and a search
  // over the first page alone never found "Strategy Designer" (owner 2026-10-31, "new members cannot be added").
  // Employees search the same way (review W-25): the 101st was unfindable.
  const term = useDebouncedValue(search.trim(), 250);
  const personas = useQuery({
    ...listTemplatesOptions({ query: { ...PAGE, ...(term ? { q: term } : {}) } }),
    enabled: open && source === 'persona',
    placeholderData: keepPreviousData,
  });
  const employees = useQuery({
    ...listEmployeesOptions({ query: { ...PAGE, enabled: true, ...(term ? { q: term } : {}) } }),
    enabled: open && source === 'workforce_member',
    placeholderData: keepPreviousData,
  });
  const people = useQuery({
    ...listOrgMembersOptions({ path: { id: organizationId ?? 0 } }),
    enabled: open && source === 'user' && canSeePeople && organizationId !== null,
  });
  const keys = useQuery({
    ...listApiKeysOptions({ query: { scope: 'user' } }),
    enabled: open && source === 'external_agent' && canSeeKeys,
  });

  const invite = useMutation({
    ...inviteToConversationMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: listConversationMembersQueryKey({ path: { id: conversationId } }),
      });
    },
    onError: (error) => notify(error.message || 'Could not add the member.', 'danger'),
  });

  const present = new Set(members.map((member) => `${member.memberType}:${member.memberId}`));
  const memberUsers = new Map(
    members.filter((member) => member.memberType === 'user').map((member) => [member.memberId, member.name]),
  );

  const sources: Record<JoinableType, { query: { isLoading: boolean; isError: boolean }; candidates: Candidate[] }> = {
    persona: {
      query: personas,
      candidates: (personas.data?.data ?? [])
        .filter((template) => !template.disabled)
        .map((template) => ({ id: template.id, name: template.title, detail: template.goal })),
    },
    workforce_member: {
      query: employees,
      candidates: (employees.data?.data ?? []).map((employee) => ({
        id: employee.id,
        name: employee.name ?? employee.slug ?? `Employee ${employee.id}`,
        detail: employee.mandate ?? null,
      })),
    },
    user: {
      query: people,
      candidates: (people.data?.members ?? [])
        .filter((person) => person.isActive !== false)
        .map((person) => ({
          id: person.id,
          name: person.fullName ?? person.username ?? person.email,
          detail: person.email,
        })),
    },
    external_agent: {
      query: keys,
      // A key joins only a conversation its person is in, and acts as that person.
      candidates: (keys.data ?? [])
        .filter((key) => key.revokedAt === null && key.userId !== null && memberUsers.has(key.userId))
        .map((key) => ({
          id: key.id,
          name: key.name,
          detail: `Acts as ${memberUsers.get(key.userId ?? 0) ?? 'its person'}`,
        })),
    },
  };
  const current = sources[source];
  const offered = current.candidates.filter(
    (candidate) => !present.has(`${source}:${candidate.id}`) && matches(candidate, search),
  );

  const tabs = [
    { id: 'persona', label: 'Personas' },
    { id: 'workforce_member', label: 'Employees' },
    ...(canSeePeople ? [{ id: 'user', label: 'People' }] : []),
    ...(canSeeKeys ? [{ id: 'external_agent', label: 'Agent keys' }] : []),
  ];

  const add = (candidate: Candidate) =>
    invite.mutate(
      { path: { id: conversationId }, body: { memberType: source, memberId: candidate.id } },
      { onSuccess: () => notify(`${candidate.name} joined the conversation.`, 'success') },
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a member"
      description="A member receives only the messages addressed to it, so adding one runs nothing by itself."
      maxWidth="lg"
    >
      <div className="flex flex-col gap-3">
        <Tabs
          tabs={tabs}
          activeTab={source}
          onChange={(id) => {
            setSource(id as JoinableType);
            setSearch('');
          }}
          variant="segmented"
          aria-label="Kind of member"
        />
        <SearchBar value={search} onChange={setSearch} placeholder="Search by name" />
        {current.query.isLoading ? (
          <SkeletonList count={4} label="Loading candidates" />
        ) : current.query.isError ? (
          <Callout tone="danger">Could not load the list. Close the dialog and try again.</Callout>
        ) : offered.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No one to add"
            description={search ? 'Nothing matches the search.' : 'Everyone of this kind is already a member.'}
          />
        ) : (
          <ItemList
            aria-label="Members you can add"
            className="max-h-[50vh] overflow-y-auto"
            items={offered.map((candidate) => ({
              id: String(candidate.id),
              leading: <Avatar kind={source === 'user' ? 'human' : 'ai'} label={candidate.name} size="sm" />,
              title: candidate.name,
              ...(candidate.detail ? { subtitle: <span className="line-clamp-1">{candidate.detail}</span> } : {}),
              value: (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={invite.isPending && invite.variables?.body?.memberId === candidate.id}
                  disabled={invite.isPending}
                  onClick={() => add(candidate)}
                  aria-label={`Add ${candidate.name}`}
                >
                  Add
                </Button>
              ),
            }))}
          />
        )}
      </div>
    </Modal>
  );
}
